import {
  FilterInstance,
  FilterSet,
  SearchTemplate,
  SupportedDisplayFilterType,
} from '../../../../../../generic-search-common/src'
import React, { KeyboardEventHandler, useMemo } from 'react'
import { FilterProps } from '../../utils'
import { FilterDate } from '../filter-date'
import { FilterDateRange } from '../filter-date-range'
import { FilterSelect } from '../filter-select'
import { FilterText } from '../filter-text'
import { FilterTypeahead } from '../filter-typeahead'
import { FilterNumericRange } from '../filter-numeric-range'
import { Button } from '@msaf/core-react'
import { RequestOptions } from '@msaf/generic-search-common'

export interface FilterGroupProps {
  filterKey: string
  searchTemplate: SearchTemplate
  query: (FilterInstance | FilterSet)[]
  addValue: () => void
  setValue: (index: number, value: string | FilterSet) => void
  removeValue: (index: number) => void
  search: () => void
  searchTypeKey: string
  requestOptions?: RequestOptions
  dateFormat: string
  handleKeyboardEvent: KeyboardEventHandler
}

export function FilterGroup({
  filterKey,
  searchTemplate,
  query,
  addValue,
  setValue,
  removeValue,
  search,
  searchTypeKey,
  requestOptions,
  dateFormat,
  handleKeyboardEvent,
}: FilterGroupProps) {
  const template = useMemo(() => {
    return searchTemplate.filters.find((f) => f.filterKey === filterKey)
  }, [filterKey, searchTemplate])

  // If there is no matching filter key in the search template, or
  // there are no filters in the filter query, render nothing
  if (template === undefined || query.length === 0) {
    return null
  }

  const elementMap: Record<SupportedDisplayFilterType, React.ElementType<FilterProps>> = {
    text: FilterText,
    date: FilterDate,
    'date-range': FilterDateRange,
    select: FilterSelect,
    typeahead: FilterTypeahead,
    'typeahead-selector': FilterTypeahead,
    'numeric-range': FilterNumericRange,
  }

  if (!(template.type in elementMap)) {
    throw new Error(`Filter type '${template.type}' is not supported.`)
  }
  const Filter = elementMap[template.type as SupportedDisplayFilterType]

  const labelId = `search-filter-input-label-${filterKey}`

  return (
    <div data-test-filter-input-group={filterKey} className='generic-search-filter__input-group'>
      <div id={labelId} className='generic-search-filter__input-label'>
        {template?.label}
      </div>

      {query.map((filter, index) => (
        <div key={`${filterKey}-filter-${index}`} className='generic-search-filter__input'>
          <Filter
            template={template}
            filterKey={filterKey}
            search={search}
            labelledBy={labelId}
            setValue={(value: string | FilterSet) => setValue(index, value)}
            value={'filterKey' in filter ? filter.filterValue ?? '' : filter}
            requestOptions={requestOptions}
            searchTypeKey={searchTypeKey}
            dateFormat={dateFormat}
            onKeyDown={handleKeyboardEvent}
          />
          <Button
            onClick={() => removeValue(index)}
            buttonStyle='secondary'
            icon='trash'
            iconAriaLabel='Remove filter'
          />

          {index === query.length - 1 ? (
            <Button buttonStyle='secondary' icon='add' iconAriaLabel='Add filter' onClick={() => addValue()} />
          ) : (
            // If no add button is required place a spacer to ensure alignment
            <div className='generic-search-filter__input-action generic-search-filter__input-action--empty'>
              <svg height='24px' width='24px'></svg>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
